import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp, collection, getDocs, query, limit } from "firebase/firestore";
import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FiUser,
  FiMail,
  FiLock,
  FiEye,
  FiEyeOff,
  FiArrowRight,
  FiBriefcase,
  FiBookOpen,
  FiArrowLeft,
  FiCheckCircle
} from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import { motion, AnimatePresence } from "framer-motion";
import { auth, db } from "../../components/firebase";
import { useTheme } from "../../context/ThemeContext";
import { cn } from "../../lib/utils";
import logo from "../../assets/LOGO SnapED.png";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";

// Modern Input Component wrapper using shadcn Input
const InputField = ({ label, icon: Icon, type, name, value, onChange, placeholder, error, isPassword, showPassword, togglePassword }) => (
  <div className="flex flex-col gap-1.5 w-full">
    <label className="text-sm font-semibold text-foreground/90 ml-1">{label}</label>
    <div className="relative group flex items-center">
      <div className="absolute left-3.5 z-10 text-muted-foreground group-focus-within:text-primary transition-colors">
        <Icon size={18} />
      </div>
      <Input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={cn(
          "pl-10 pr-10 py-6 bg-background border-border/60 hover:border-primary/50 focus-visible:ring-primary/20 focus-visible:border-primary transition-all text-[15px] rounded-xl",
          error && "border-destructive/80 focus-visible:ring-destructive/20 focus-visible:border-destructive"
        )}
      />
      {isPassword && (
        <button
          type="button"
          onClick={togglePassword}
          className="absolute right-3.5 z-10 text-muted-foreground hover:text-foreground transition-colors outline-none"
        >
          {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
        </button>
      )}
    </div>
    <AnimatePresence>
      {error && (
        <motion.p
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="text-destructive text-[12px] font-medium ml-1"
        >
          {error}
        </motion.p>
      )}
    </AnimatePresence>
  </div>
);

function Register({ isModal = false, onToggleMode }) {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    collegeName: "",
    role: "student"
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [trustedUsers, setTrustedUsers] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const googleProvider = new GoogleAuthProvider();

  useEffect(() => {
    const fetchRandomUsers = async () => {
      try {
        const usersRef = collection(db, "Users");
        // Fetch up to 10 users to pick ones with photos
        const q = query(usersRef, limit(10));
        const querySnapshot = await getDocs(q);
        const users = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.photo) {
            users.push(data.photo);
          }
        });
        
        // Pick top 4, or fill with default avatar if not enough
        const avatars = users.slice(0, 4);
        while (avatars.length < 4) {
          avatars.push(`https://api.dicebear.com/7.x/avataaars/svg?seed=${Math.random()}`);
        }
        setTrustedUsers(avatars);
      } catch (error) {
        console.error("Error fetching users:", error);
        // Fallback avatars
        setTrustedUsers([
           `https://api.dicebear.com/7.x/avataaars/svg?seed=user1`,
           `https://api.dicebear.com/7.x/avataaars/svg?seed=user2`,
           `https://api.dicebear.com/7.x/avataaars/svg?seed=user3`,
           `https://api.dicebear.com/7.x/avataaars/svg?seed=user4`,
        ]);
      }
    };

    fetchRandomUsers();
  }, []);



  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = formData.role === 'company' ? "Rep name is required" : "Full name is required";
    if (!formData.collegeName.trim()) newErrors.collegeName = formData.role === 'company' ? "Company name is required" : "College is required";
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) newErrors.email = "Valid email is required";
    if (formData.password.length < 6) newErrors.password = "Min 6 characters required";
    if (!acceptedTerms) newErrors.terms = "Please accept the terms to continue";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;
      await sendEmailVerification(user);

      const normalizedRole = formData.role === 'company' ? 'company' : 'student';

      await setDoc(doc(db, "Users", user.uid), {
        uid: user.uid,
        email: user.email,
        fullName: formData.fullName,
        college: formData.collegeName,
        collegeName: formData.collegeName,
        role: normalizedRole,
        photo: "",
        emailVerified: false,
        createdAt: serverTimestamp(),
        provider: "email"
      });

      if (normalizedRole === 'company') {
        await setDoc(doc(db, "Companies", user.uid), {
          uid: user.uid,
          companyName: formData.collegeName,
          representativeName: formData.fullName,
          email: user.email,
          createdAt: serverTimestamp(),
          status: 'pending'
        });
      }

      toast.success("Account created! Please check your email to verify.", { position: "top-center" });
      if (isModal) {
        onToggleMode();
      } else {
        setTimeout(() => navigate("/login"), 3000);
      }
    } catch (error) {
      toast.error(error.message, { position: "top-center" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const userRef = doc(db, "Users", user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email,
          fullName: user.displayName || "Google User",
          collegeName: "Not specified",
          role: "student",
          photo: user.photoURL || "",
          emailVerified: user.emailVerified,
          createdAt: serverTimestamp(),
          provider: "google"
        });
      }
      toast.success("Welcome! Signed in via Google.", { position: "top-center" });
      const from = location.state?.from;
      const redirectPath = from || "/home";
      setTimeout(() => navigate(redirectPath), 1500);
    } catch (error) {
      toast.error("Google sign in failed.", { position: "top-center" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  // The actual form content, separated to be reused in Modal or Full Page
  const FormContent = () => (
    <div className="w-full max-w-[440px] mx-auto overflow-hidden sm:overflow-visible">
      <div className="text-center mb-8 px-4 sm:px-0">
        {!isModal && (
          <Link to="/" className="inline-block mb-6 lg:hidden">
            <img src={logo} alt="SnapED" className="h-10 mx-auto drop-shadow-sm" />
          </Link>
        )}
        <h1 className="text-3xl font-bold tracking-tight text-foreground mb-2">
          Create an account
        </h1>
        <p className="text-muted-foreground text-[15px]">
          Join SnapED to start your journey
        </p>
      </div>

      <div className="bg-card/40 backdrop-blur-xl sm:bg-transparent sm:backdrop-blur-none rounded-3xl p-6 sm:p-0 border border-border/50 sm:border-none shadow-xl sm:shadow-none w-full box-border">
        
        {/* Role Selection Tabs */}
        <div className="flex bg-muted/40 p-1.5 rounded-2xl mb-8 relative">
          <motion.div
            className="absolute inset-y-1.5 w-[calc(50%-6px)] bg-background rounded-xl shadow-sm border border-border/50"
            animate={{ left: formData.role === 'student' ? '6px' : 'calc(50% + 0px)' }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
          />
          <button
            type="button"
            onClick={() => setFormData(p => ({ ...p, role: 'student' }))}
            className={cn(
              "flex-1 py-3 px-4 rounded-xl text-sm font-bold z-10 flex items-center justify-center gap-2 transition-colors",
              formData.role === 'student' ? "text-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            <FiBookOpen size={16} /> <span className="hidden sm:inline">I am a</span> Student
          </button>
          <button
            type="button"
            onClick={() => setFormData(p => ({ ...p, role: 'company' }))}
            className={cn(
              "flex-1 py-3 px-4 rounded-xl text-sm font-bold z-10 flex items-center justify-center gap-2 transition-colors",
              formData.role === 'company' ? "text-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            <FiBriefcase size={16} /> Company
          </button>
        </div>

        <form onSubmit={handleRegister} className="flex flex-col gap-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <InputField
              label={formData.role === 'company' ? "Representative Name" : "Full Name"}
              icon={FiUser}
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder={formData.role === 'company' ? "John Doe" : "John Doe"}
              error={errors.fullName}
            />

            <InputField
              label={formData.role === 'company' ? "Company Name" : "College Name"}
              icon={formData.role === 'company' ? FiBriefcase : FiBookOpen}
              name="collegeName"
              value={formData.collegeName}
              onChange={handleChange}
              placeholder={formData.role === 'company' ? "Company Inc" : "University Name"}
              error={errors.collegeName}
            />
          </div>

          <InputField
            label="Email Address"
            icon={FiMail}
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="name@example.com"
            error={errors.email}
          />

          <InputField
            label="Password"
            icon={FiLock}
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Min. 6 characters"
            isPassword
            showPassword={showPassword}
            togglePassword={() => setShowPassword(!showPassword)}
            error={errors.password}
          />

          <div className="space-y-1.5 mt-1">
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative flex items-center justify-center w-5 h-5 mt-0.5 rounded border border-border group-hover:border-primary transition-colors bg-background shrink-0">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="peer sr-only"
                />
                <FiCheckCircle className={cn(
                  "absolute text-primary opacity-0 scale-50 transition-all duration-200 pointer-events-none",
                  acceptedTerms && "opacity-100 scale-100"
                )} size={16} />
              </div>
              <span className="text-[13px] text-muted-foreground leading-relaxed">
                By creating an account, you agree to our{" "}
                <Link to="/terms-and-conditions" className="text-foreground font-semibold hover:text-primary transition-colors underline underline-offset-2 break-words">Terms of Service</Link>
                {" "}and{" "}
                <Link to="/privacy-policy" className="text-foreground font-semibold hover:text-primary transition-colors underline underline-offset-2 break-words">Privacy Policy</Link>.
              </span>
            </label>
            <AnimatePresence>
              {errors.terms && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="text-destructive text-[12px] font-medium ml-8"
                >
                  {errors.terms}
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            size="lg"
            className="w-full text-[15px] font-bold h-12 rounded-xl mt-2 bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20 transition-all"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <div className="h-4 w-4 rounded-full border-2 border-primary-foreground border-r-transparent animate-spin"/>
                Creating account...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                Sign Up <FiArrowRight size={18} />
              </span>
            )}
          </Button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border/60"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background sm:bg-transparent sm:backdrop-blur-none px-4 font-semibold text-muted-foreground uppercase tracking-widest bg-card/40 sm:bg-background">
                or continue with
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            size="lg"
            disabled={isLoading}
            onClick={handleGoogleSignIn}
            className="w-full rounded-xl h-12 bg-background hover:bg-muted/50 border-border/60 font-semibold text-[14px] transition-all"
          >
            <FcGoogle size={20} className="mr-2 shrink-0" />
            Google
          </Button>

          <p className="text-center mt-6 text-sm text-muted-foreground">
            Already have an account?{" "}
            {isModal ? (
              <button
                onClick={onToggleMode}
                className="text-primary font-bold hover:underline transition-all"
              >
                Sign In
              </button>
            ) : (
              <Link to="/login" state={{ from: location.state?.from }} className="text-primary font-bold hover:underline transition-all">
                Sign In
              </Link>
            )}
          </p>
        </form>
      </div>
    </div>
  );

  // Modal view
  if (isModal) {
    return (
      <div className="w-full py-2">
        <FormContent />
      </div>
    );
  }

  // Full page split layout view (Desktop) & centered (Mobile)
  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row relative selection:bg-primary/20 w-full overflow-x-hidden">
      
      {/* Back Button */}
      <motion.button
        onClick={() => navigate('/')}
        whileHover={{ x: -4 }}
        className="absolute top-6 left-6 lg:left-8 z-50 flex items-center justify-center w-10 h-10 lg:w-auto lg:h-auto lg:px-4 lg:py-2 bg-background/50 border border-border/50 lg:border-border rounded-full lg:rounded-xl text-foreground text-sm font-semibold backdrop-blur-md hover:bg-muted/50 transition-all shadow-sm group"
      >
        <FiArrowLeft className="lg:mr-2 group-hover:-translate-x-1 transition-transform" />
        <span className="hidden lg:inline">Back to Home</span>
      </motion.button>

      {/* Left side - Branding (Hidden on mobile) */}
      <div className="hidden lg:flex w-1/2 relative bg-zinc-950 overflow-hidden items-center justify-center p-12">
        {/* Decorative background elements */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/30 via-zinc-950/20 to-zinc-950"></div>
          <motion.div
            animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-primary/20 blur-[120px]"
          />
          <motion.div
            animate={{ scale: [1, 1.5, 1], rotate: [0, -90, 0] }}
            transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full bg-blue-500/10 blur-[150px]"
          />
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-lg">
          <Link to="/">
            <img src={logo} alt="SnapED" className="h-16 mb-12 drop-shadow-[0_0_20px_rgba(34,197,94,0.3)] filter-none" />
          </Link>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight tracking-tight">
            Discover your next <br/>
            <span className="text-primary inline-block bg-clip-text">opportunity</span> today.
          </h2>
          <p className="text-zinc-400 text-lg leading-relaxed mb-12 max-w-md">
            Join thousands of students and companies bridging the gap between talent and opportunity.
          </p>

          <div className="flex items-center gap-6 text-sm font-medium text-zinc-300">
            <div className="flex -space-x-3">
              {trustedUsers.map((avatarUrl, i) => (
                <div key={i} className={`w-10 h-10 rounded-full border-2 border-zinc-950 bg-zinc-800 flex items-center justify-center overflow-hidden`}>
                  <img src={avatarUrl} alt="User" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
            <p>Trusted by <span className="text-white font-bold">10,000+</span> users</p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-12 lg:p-16 relative">
        <div className="absolute inset-0 bg-background lg:hidden -z-10">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-primary/5 blur-[100px] translate-x-1/2 -translate-y-1/2" />
        </div>
        
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <FormContent />
          </motion.div>
        </div>
      </div>

      <ToastContainer theme={isDarkMode ? "dark" : "light"} limit={1} position="top-center" />
    </div>
  );
}

export default Register;